import React, { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import GenerativeIndicatorIcon from '@atlaskit/icon-lab/core/generative-indicator';
import { useUIStore } from '@atlassian/conversation-assistant-store';
import { type ConversationAction, HandleActionType } from '@atlassian/rovo-conversation-actions';
import { useToggleConversationAssistant } from '@confluence/conversation-assistant/entry-points/useToggleConversationAssistant.ts';

const messages = defineMessages({
	insightsNudgeTitle: {
		id: 'page-rovo-config-common.insights-nudge-title.v2',
		defaultMessage: 'Explore your Rovo insights',
		description: 'Title for the Rovo Insights weekly nudge that appears next to the Rovo button',
	},
	insightsNudgeIconLabel: {
		id: 'page-rovo-config-common.insights-nudge-icon-label',
		defaultMessage: 'Insights icon',
		description: 'Accessible label for the icon in the Rovo Insights weekly nudge',
	},
});

/**
 * Hook that creates and returns the Insights weekly nudge action.
 */
export function useInsightsNudgeAction(): ConversationAction {
	const { setActiveMenu } = useUIStore();
	const { formatMessage } = useIntl();
	const { mode, toggleConversationAssistant } = useToggleConversationAssistant();

	const onClick = useCallback(() => {
		if (!mode) {
			toggleConversationAssistant();
		}
		setActiveMenu('pulse');
	}, [mode, toggleConversationAssistant, setActiveMenu]);

	return useMemo(
		() => ({
			id: 'rovo-insights-nudge',
			title: formatMessage(messages.insightsNudgeTitle),
			icon: () => (
				<GenerativeIndicatorIcon
					size="small"
					label={formatMessage(messages.insightsNudgeIconLabel)}
				/>
			),
			onClick,
			type: 'ai-action',
			handleType: HandleActionType.Base,
			nudgeContext: {
				id: 'rovo-insights',
			},
		}),
		[onClick, formatMessage],
	);
}
